/**
 * ProtokollTable — wiederverwendbare Aktionsprotokoll-Tabelle
 *
 * Wird zweimal eingesetzt:
 *   1. Allgemeine Ansicht (/protokoll) mit Filterleiste (`showFilters`).
 *   2. Profil-Tab in PB-/BK-Detail (`fixedQuery={{ user_id }}`, `compact`).
 *
 * Datenquelle: src/lib/activityProtocol.ts (GET /v2/admin/activity-protocol).
 * Jede Filteränderung setzt die Seite auf 1 zurück; die Volltextsuche ist
 * mit 300 ms debounced. Filter-State liegt nur im Komponenten-State (nicht
 * in der URL).
 *
 * Hinweis zur Ladelogik: Lade-/Ergebnis-Zustand wird über einen Query-Key
 * abgeleitet (Ergebnis trägt den Key seiner Anfrage), statt `setLoading`
 * synchron im Effekt aufzurufen; so bleiben die react-hooks-Regeln erfüllt.
 */
import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, Input } from '../../components/ui';
import { Icon } from '../../components/icons';
import { BRAND, getThemeColors } from '../../lib/theme';
import { AppLink } from '../../lib/openLink';
import { DateInput } from '../../components/DateInput';
import {
  listActivityProtocol,
  getActivityProtocolOptions,
  describeEntry,
  extractChangeGroups,
  formatChangeField,
  moduleLabel,
  objectLabel,
  summarizeChanges,
  PROTOCOL_MODULES,
  type ActivityProtocolEntry,
  type ActivityProtocolOptions,
  type ActivityProtocolQuery,
  type ActivityProtocolMeta,
} from '../../lib/activityProtocol';
import type { BadgeTone } from '../../types';

/* ─── Typen ─────────────────────────────────────────────────────────────── */

export type ProtokollColumn = 'time' | 'actor' | 'action' | 'module' | 'context' | 'entity' | 'route' | 'details';

const ALL_COLUMNS: ProtokollColumn[] = ['time', 'actor', 'action', 'module', 'context', 'entity', 'route', 'details'];

interface Props {
  dark: boolean;
  /** Feste Query-Anteile, z. B. `{ user_id }` im Profil-Modus. */
  fixedQuery?: ActivityProtocolQuery;
  /** Filterleiste anzeigen (allgemeine Ansicht). */
  showFilters?: boolean;
  /** Kompakte Darstellung (Profil-Tab). */
  compact?: boolean;
  hiddenColumns?: ProtokollColumn[];
  /**
   * Batch-13 Paket E — injizierbare Datenquelle. Default: `listActivityProtocol`
   * (GET /v2/admin/activity-protocol, unverändertes Verhalten für /protokoll
   * und den personenbezogenen Profil-Modus). `ProtokollTab` übergibt hier bei
   * einem Familienkonto `familyAccountProtocol.list(accountId, …)` (Konto-
   * Umfang statt einer Person). MUSS referenzstabil sein (`useCallback` beim
   * Aufrufer) — steht in der Effekt-Dependency-Liste; ein neuer Funktions-
   * Wert bei jedem Render würde den Request in einer Schleife wiederholen.
   */
  fetcher?: (query: ActivityProtocolQuery) => Promise<{ data: ActivityProtocolEntry[]; meta: ActivityProtocolMeta }>;
}

interface Filters {
  from: string;
  to: string;
  actor_type: string;
  actor_id: string;
  context: string;
  action: string;
  module: string;
  /** Hintergrund-Aktivität (meta.quiet) zusätzlich anzeigen. */
  show_quiet: boolean;
  search: string;
  entity_id: string;
}

const EMPTY_FILTERS: Filters = {
  from: '', to: '', actor_type: '', actor_id: '', context: '', action: '', module: '', show_quiet: false, search: '', entity_id: '',
};

interface LoadResult {
  key: string;
  data: ActivityProtocolEntry[];
  meta: ActivityProtocolMeta | null;
  error: boolean;
}

const PER_PAGE = 25;
const PER_PAGE_OPTIONS = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;

/* ─── Helfer ────────────────────────────────────────────────────────────── */

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** dd.MM.yyyy HH:mm:ss in lokaler Zeit. */
export function formatProtocolTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatDuration(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${pad(seconds % 60)}`;
}

function isCallAction(action: string): boolean {
  return action.startsWith('3cx_call_');
}

function actionTone(action: string): BadgeTone {
  if (isCallAction(action)) return 'blue';
  // Fehlgeschlagene System-/Stage-Aktionen sind der Grund, warum jemand ins
  // Protokoll schaut — sie werden wie Löschungen rot hervorgehoben.
  if (action.endsWith('_failed')) return 'error';
  switch (action) {
    case 'create': return 'success';
    case 'update': return 'info';
    case 'delete': return 'error';
    case 'login':  return 'brand';
    case 'logout': return 'brand';
    case 'phase_changed':
    case 'phase_auto_advance':
      return 'info';
    case 'anfrage_uebernommen':
    case 'anfrage_wiederhergestellt':
    case 'stage_action_task_created':
    case 'stage_action_email_sent':
    case 'docusign_envelope_sent':
    case 'mail_sent':
      return 'success';
    case 'anfrage_abgebrochen':
      return 'error';
    default:
      return 'neutral';
  }
}

function actorTone(type: ActivityProtocolEntry['actor_type']): BadgeTone {
  if (type === 'admin') return 'brand';
  if (type === 'user') return 'info';
  return 'neutral';
}

function toInt(v: string): number | undefined {
  if (!/^\d+$/.test(v.trim())) return undefined;
  return Number(v.trim());
}

/** Baut die effektive Query; leere Werte werden weggelassen. */
function buildQuery(filters: Filters, fixed: ActivityProtocolQuery | undefined, page: number, perPage: number, showFilters: boolean): ActivityProtocolQuery {
  const q: ActivityProtocolQuery = {};
  if (filters.actor_type) q.actor_type = filters.actor_type as ActivityProtocolQuery['actor_type'];
  const actorId = toInt(filters.actor_id);
  if (actorId !== undefined && (filters.actor_type === '' || filters.actor_type === 'admin')) q.actor_id = actorId;
  if (filters.context) q.context = filters.context as ActivityProtocolQuery['context'];
  if (filters.action) q.action = filters.action;
  if (filters.module) q.module = filters.module;
  // Nur die allgemeine Ansicht (mit Filterleiste/Checkbox) blendet Hintergrund-Aktivität standardmäßig aus.
  if (showFilters && !filters.show_quiet) q.hide_quiet = true;
  const entityId = toInt(filters.entity_id);
  if (entityId !== undefined) q.entity_id = entityId;
  if (filters.search.trim()) q.search = filters.search.trim();
  if (filters.from) q.from = filters.from;
  if (filters.to) q.to = filters.to;
  if (fixed) {
    for (const [k, v] of Object.entries(fixed)) {
      if (v === undefined || v === null || v === '') continue;
      (q as Record<string, unknown>)[k] = v;
    }
  }
  q.page = page;
  q.per_page = perPage;
  return q;
}

function metaValueToString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
}

/* ─── Komponente ────────────────────────────────────────────────────────── */

export function ProtokollTable({ dark, fixedQuery, showFilters = false, compact = false, hiddenColumns = [], fetcher }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const ids = useId();

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE);
  const [result, setResult] = useState<LoadResult | null>(null);
  const [options, setOptions] = useState<ActivityProtocolOptions | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const query = buildQuery(filters, fixedQuery, page, perPage, showFilters);
  const queryKey = JSON.stringify(query);
  // Default bleibt die bisherige Funktion (stabile Modul-Referenz) — /protokoll
  // und der personenbezogene Profil-Modus laufen dadurch unverändert weiter.
  const fetchFn = fetcher ?? listActivityProtocol;

  // Daten laden, sobald sich die effektive Query ändert. Das Ergebnis trägt
  // den Key seiner Anfrage; veraltete Antworten werden verworfen.
  useEffect(() => {
    let cancelled = false;
    fetchFn(JSON.parse(queryKey) as ActivityProtocolQuery)
      .then(res => {
        if (cancelled) return;
        setResult({ key: queryKey, data: res.data, meta: res.meta, error: false });
        setExpanded(new Set());
      })
      .catch(() => {
        if (cancelled) return;
        setResult({ key: queryKey, data: [], meta: null, error: true });
      });
    return () => { cancelled = true; };
  }, [queryKey, fetchFn]);

  // Filter-Optionen nur für die Filterleiste laden.
  useEffect(() => {
    if (!showFilters) return;
    let cancelled = false;
    getActivityProtocolOptions()
      .then(o => { if (!cancelled) setOptions(o); })
      .catch(() => { /* Dropdowns bleiben leer, Freitext-Filter funktionieren weiter */ });
    return () => { cancelled = true; };
  }, [showFilters]);

  // Volltextsuche debounced in die Filter übernehmen.
  useEffect(() => {
    const id = setTimeout(() => {
      setFilters(f => (f.search === searchInput ? f : { ...f, search: searchInput }));
      setPage(p => (p === 1 ? p : 1));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  const loading = result === null || result.key !== queryKey;
  const rows = result?.data ?? [];
  const meta = result?.meta ?? null;
  const error = !loading && (result?.error ?? false);

  const setFilter = (key: keyof Filters, value: string | boolean) => {
    setFilters(f => {
      const next = { ...f, [key]: value };
      // Akteur-Auswahl gilt nur für Admins
      if (key === 'actor_type' && value !== '' && value !== 'admin') next.actor_id = '';
      return next;
    });
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchInput('');
    setPage(1);
  };

  const toggleExpanded = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const columns = ALL_COLUMNS.filter(c => !hiddenColumns.includes(c));
  const cellPad = compact ? '7px 10px' : '10px 14px';

  const selectStyle: React.CSSProperties = {
    padding: '8px 10px', borderRadius: 8, border: `1px solid ${t.border}`,
    background: t.inputBg, color: t.text, fontSize: 13,
    fontFamily: 'Outfit,sans-serif', cursor: 'pointer', width: '100%',
  };
  // Rahmen wie <Input> aus components/ui (Datum „TT.MM.JJJJ" via DateInput).
  const dateStyle: React.CSSProperties = {
    border: `1px solid ${t.border}`, borderRadius: 9, padding: '9px 12px',
    fontSize: 13, color: t.heading, background: t.inputBg, fontFamily: 'Outfit, sans-serif',
  };
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 500, color: t.muted };
  const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5, flex: '1 1 150px', minWidth: 140 };
  const thStyle: React.CSSProperties = {
    textAlign: 'left', padding: cellPad, color: t.muted, fontWeight: 600, fontSize: 11,
    letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap',
  };
  const tdStyle: React.CSSProperties = { padding: cellPad, color: t.text, verticalAlign: 'top' };

  const actionLabel = (action: string): string => {
    if (isCallAction(action)) {
      const status = action.slice('3cx_call_'.length);
      return tr(`protokoll.call.status.${status}`, {
        defaultValue: tr('protokoll.call.status.other', { status }),
      });
    }
    return tr(`protokoll.action.${action}`, { defaultValue: action });
  };

  const renderEntity = (e: ActivityProtocolEntry) => {
    if (e.entity_id === null && !e.entity_label) return <span style={{ color: t.placeholder }}>·</span>;
    const label = e.entity_label ?? `#${e.entity_id}`;
    let page: 'pflege' | 'betreuung' | null = null;
    if (e.entity_type === 'pb' && e.entity_konto != null && e.entity_user_id != null) {
      page = 'pflege';
    } else if (e.entity_type === 'bk' && e.entity_user_id != null) {
      page = 'betreuung';
    }
    if (!page) return <span style={{ color: e.entity_label ? t.text : t.muted }}>{label}</span>;
    return (
      <AppLink
        page={page}
        sub={String(e.entity_user_id)}
        style={{ textDecoration: 'none', cursor: 'pointer', color: BRAND, fontWeight: 500, fontSize: 13, fontFamily: 'Outfit,sans-serif', textAlign: 'left' }}
      >
        {label}
      </AppLink>
    );
  };

  // Bei Anlage/Löschung nennt der Satz keine Werte → Kurzfassung der angelegten/gelöschten Felder.
  const renderSummary = (e: ActivityProtocolEntry) => {
    const groups = extractChangeGroups(e);
    const g = groups[0];
    if (!g || (g.event !== 'created' && g.event !== 'deleted')) return null;
    const text = summarizeChanges(groups, tr);
    return text
      ? <div data-testid="protokoll-summary" style={{ fontSize: 12, color: t.muted, marginTop: 3, wordBreak: 'break-word' }}>{text}</div>
      : null;
  };

  const renderCallInfo = (e: ActivityProtocolEntry) => {
    if (!isCallAction(e.action) || !e.meta) return null;
    const direction = typeof e.meta.direction === 'string' ? e.meta.direction : null;
    const duration = typeof e.meta.duration_seconds === 'number' ? e.meta.duration_seconds : null;
    const parts: string[] = [];
    if (direction === 'inbound' || direction === 'outbound') parts.push(tr(`protokoll.call.direction.${direction}`));
    if (duration !== null) parts.push(formatDuration(duration));
    if (parts.length === 0) return null;
    return <div style={{ fontSize: 11, color: t.muted, marginTop: 3 }}>{parts.join(' · ')}</div>;
  };

  // Technische meta-Schlüssel, die die Änderungsliste bzw. die Spalten schon abbilden.
  const HIDDEN_META_KEYS = new Set(['changes', 'related', 'module', 'quiet', 'event', 'truncated']);

  const renderChangeGroups = (e: ActivityProtocolEntry) => {
    const groups = extractChangeGroups(e);
    if (groups.length === 0) return null;
    const truncated = e.meta?.truncated === true;
    return (
      <div data-testid="protokoll-changes" style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
          {tr('protokoll.changes.title')}
        </div>
        {groups.map((g, gi) => (
          <div key={gi} style={{ marginBottom: 8 }}>
            {(gi > 0 || g.label || g.object) && (
              <div style={{ fontSize: 12, fontWeight: 600, color: t.text, marginBottom: 2 }}>
                {gi > 0 && <span style={{ color: t.muted, fontWeight: 400 }}>{tr('protokoll.changes.related')}: </span>}
                {g.module ? `${moduleLabel(g.module, tr)} · ` : ''}
                {g.object ? objectLabel(g.object, tr) : ''}
                {g.label ? ` „${g.label}“` : g.objectId !== null ? ` #${g.objectId}` : ''}
              </div>
            )}
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: t.text }}>
              {g.fields.map(f => (
                <li key={f.field} style={{ wordBreak: 'break-word' }}>{formatChangeField(f, g.event, tr)}</li>
              ))}
            </ul>
          </div>
        ))}
        {truncated && <div style={{ fontSize: 11, color: t.muted }}>{tr('protokoll.changes.truncated')}</div>}
      </div>
    );
  };

  const renderDetails = (e: ActivityProtocolEntry) => {
    const entries = e.meta ? Object.entries(e.meta).filter(([k]) => !HIDDEN_META_KEYS.has(k)) : [];
    const changes = renderChangeGroups(e);
    if (entries.length === 0 && !changes) {
      return <div style={{ fontSize: 12, color: t.muted }}>{tr('protokoll.details.none')}</div>;
    }
    return (
      <>
        {changes}
        {entries.length > 0 && (
          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 14px', fontSize: 12 }}>
            {entries.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt style={{ color: t.muted, fontFamily: 'monospace' }}>{k}</dt>
                <dd style={{ margin: 0, color: t.text, wordBreak: 'break-all' }}>
                  {k === 'status' && typeof v === 'number'
                    ? <Badge dark={dark} tone={v < 400 ? 'success' : 'error'}>{v}</Badge>
                    : metaValueToString(v)}
                </dd>
              </React.Fragment>
            ))}
          </dl>
        )}
      </>
    );
  };

  const showActorSelect = filters.actor_type === '' || filters.actor_type === 'admin';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {showFilters && (
        <Card dark={dark} style={{ padding: compact ? 12 : 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            <div style={fieldStyle}>
              <label htmlFor={`${ids}-from`} style={labelStyle}>{tr('protokoll.filter.from')}</label>
              <DateInput id={`${ids}-from`} value={filters.from} onChange={v => setFilter('from', v)} style={dateStyle} />
            </div>
            <div style={fieldStyle}>
              <label htmlFor={`${ids}-to`} style={labelStyle}>{tr('protokoll.filter.to')}</label>
              <DateInput id={`${ids}-to`} value={filters.to} onChange={v => setFilter('to', v)} style={dateStyle} />
            </div>
            <div style={fieldStyle}>
              <label htmlFor={`${ids}-actor-type`} style={labelStyle}>{tr('protokoll.filter.actor_type')}</label>
              <select id={`${ids}-actor-type`} value={filters.actor_type} onChange={e => setFilter('actor_type', e.target.value)} style={selectStyle}>
                <option value="">{tr('protokoll.filter.all')}</option>
                {(options?.actor_types ?? ['admin', 'user', 'system']).map(v => (
                  <option key={v} value={v}>{tr(`protokoll.actor_type.${v}`, { defaultValue: v })}</option>
                ))}
              </select>
            </div>
            {showActorSelect && (
              <div style={fieldStyle}>
                <label htmlFor={`${ids}-actor`} style={labelStyle}>{tr('protokoll.filter.actor')}</label>
                <select id={`${ids}-actor`} value={filters.actor_id} onChange={e => setFilter('actor_id', e.target.value)} style={selectStyle}>
                  <option value="">{tr('protokoll.filter.all')}</option>
                  {(options?.admins ?? []).map(a => (
                    <option key={a.id} value={String(a.id)}>{a.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={fieldStyle}>
              <label htmlFor={`${ids}-context`} style={labelStyle}>{tr('protokoll.filter.context')}</label>
              <select id={`${ids}-context`} value={filters.context} onChange={e => setFilter('context', e.target.value)} style={selectStyle}>
                <option value="">{tr('protokoll.filter.all')}</option>
                {(options?.contexts ?? ['pflege', 'betreuung']).map(v => (
                  <option key={v} value={v}>{tr(`protokoll.context.${v}`, { defaultValue: v })}</option>
                ))}
              </select>
            </div>
            <div style={fieldStyle}>
              <label htmlFor={`${ids}-action`} style={labelStyle}>{tr('protokoll.filter.action')}</label>
              <select id={`${ids}-action`} value={filters.action} onChange={e => setFilter('action', e.target.value)} style={selectStyle}>
                <option value="">{tr('protokoll.filter.all')}</option>
                {(options?.actions ?? []).map(a => (
                  <option key={a} value={a}>{actionLabel(a)}</option>
                ))}
              </select>
            </div>
            <div style={fieldStyle}>
              <label htmlFor={`${ids}-module`} style={labelStyle}>{tr('protokoll.filter.module')}</label>
              <select id={`${ids}-module`} value={filters.module} onChange={e => setFilter('module', e.target.value)} style={selectStyle}>
                <option value="">{tr('protokoll.filter.all')}</option>
                {(options?.modules ?? [...PROTOCOL_MODULES]).map(m => (
                  <option key={m} value={m}>{moduleLabel(m, tr)}</option>
                ))}
              </select>
            </div>
            <div style={fieldStyle}>
              <label htmlFor={`${ids}-entity`} style={labelStyle}>{tr('protokoll.filter.entity_id')}</label>
              <Input
                id={`${ids}-entity`} dark={dark} inputMode="numeric" pattern="[0-9]*"
                value={filters.entity_id}
                onChange={e => setFilter('entity_id', e.target.value.replace(/[^\d]/g, ''))}
              />
            </div>
            <div style={{ ...fieldStyle, flex: '2 1 220px' }}>
              <label htmlFor={`${ids}-search`} style={labelStyle}>{tr('protokoll.filter.search')}</label>
              <Input
                id={`${ids}-search`} dark={dark} value={searchInput}
                placeholder={tr('protokoll.filter.search_placeholder')}
                onChange={e => setSearchInput(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-end', paddingBottom: 9 }}>
              <input
                id={`${ids}-quiet`} type="checkbox" checked={!filters.show_quiet}
                onChange={e => setFilter('show_quiet', !e.target.checked)}
              />
              <label htmlFor={`${ids}-quiet`} style={{ ...labelStyle, cursor: 'pointer' }}>{tr('protokoll.filter.hide_quiet')}</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button dark={dark} variant="secondary" onClick={resetFilters}>{tr('protokoll.filter.reset')}</Button>
            </div>
          </div>
        </Card>
      )}

      {error && (
        <div style={{ color: '#d92d20', fontSize: 13 }}>{tr('protokoll.state.error')}</div>
      )}

      <Card dark={dark} style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', minWidth: compact ? 720 : 900, borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${t.border}`, background: t.tableHeader }}>
                {columns.map(col => (
                  <th key={col} style={{ ...thStyle, width: col === 'details' ? 48 : undefined }}>
                    {col === 'details' ? '' : tr(`protokoll.column.${col}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && !error && (
                <tr>
                  <td colSpan={columns.length} style={{ padding: '24px 14px', textAlign: 'center', color: t.muted }}>
                    {tr('protokoll.state.loading')}
                  </td>
                </tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} style={{ padding: '24px 14px', textAlign: 'center', color: t.muted }}>
                    {tr('protokoll.state.empty')}
                  </td>
                </tr>
              )}
              {!loading && rows.map(e => {
                const open = expanded.has(e.id);
                return (
                  <React.Fragment key={e.id}>
                    <tr style={{ borderBottom: open ? 'none' : `1px solid ${t.borderSoft}` }}>
                      {columns.includes('time') && (
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: t.muted, fontSize: 12 }}>
                          {formatProtocolTime(e.created_at)}
                        </td>
                      )}
                      {columns.includes('actor') && (
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <Badge dark={dark} tone={actorTone(e.actor_type)}>
                              {tr(`protokoll.actor_type.${e.actor_type}`, { defaultValue: e.actor_type })}
                            </Badge>
                            <span style={{ fontWeight: 500 }}>{e.actor_name ?? (e.actor_id != null ? `#${e.actor_id}` : '')}</span>
                          </div>
                        </td>
                      )}
                      {columns.includes('action') && (
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {isCallAction(e.action) && <Icon.Phone c={t.muted} />}
                            <Badge dark={dark} tone={actionTone(e.action)}>{actionLabel(e.action)}</Badge>
                          </div>
                          {/* Feedback 11.10.2026: lesbarer Satz wer/wann/was/woran als Hauptzeile. */}
                          <div data-testid="protokoll-sentence" style={{ fontSize: 13, color: t.text, marginTop: 4, lineHeight: 1.4, wordBreak: 'break-word' }}>
                            {describeEntry(e, tr, !fixedQuery?.user_id)}
                          </div>
                          {renderSummary(e)}
                          {renderCallInfo(e)}
                        </td>
                      )}
                      {columns.includes('module') && (
                        <td style={tdStyle}>
                          {e.module
                            ? <Badge dark={dark} tone="neutral">{moduleLabel(e.module, tr)}</Badge>
                            : <span style={{ color: t.placeholder }}>·</span>}
                          {e.meta?.quiet === true && (
                            <div style={{ marginTop: 4 }}><Badge dark={dark} tone="neutral">{tr('protokoll.quiet')}</Badge></div>
                          )}
                        </td>
                      )}
                      {columns.includes('context') && (
                        <td style={tdStyle}>
                          {e.context
                            ? tr(`protokoll.context.${e.context}`, { defaultValue: e.context })
                            : <span style={{ color: t.placeholder }}>·</span>}
                        </td>
                      )}
                      {columns.includes('entity') && (
                        <td style={tdStyle}>{renderEntity(e)}</td>
                      )}
                      {columns.includes('route') && (
                        <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11, color: t.muted, wordBreak: 'break-all' }}>
                          {e.method && <span style={{ fontWeight: 600, marginRight: 6 }}>{e.method}</span>}
                          <span>{e.route}</span>
                        </td>
                      )}
                      {columns.includes('details') && (
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <button
                            type="button"
                            aria-label={open ? tr('protokoll.details.hide') : tr('protokoll.details.show')}
                            title={open ? tr('protokoll.details.hide') : tr('protokoll.details.show')}
                            aria-expanded={open}
                            onClick={() => toggleExpanded(e.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6 }}
                          >
                            <Icon.Chevron c={t.muted} dir={open ? 'up' : 'down'} />
                          </button>
                        </td>
                      )}
                    </tr>
                    {open && (
                      <tr style={{ borderBottom: `1px solid ${t.borderSoft}` }}>
                        <td colSpan={columns.length} style={{ padding: `4px ${compact ? 10 : 14}px 12px`, background: t.cardMuted }}>
                          {renderDetails(e)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {meta && meta.total > 0 && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
            gap: 10, padding: compact ? '8px 10px' : '10px 14px', borderTop: `1px solid ${t.border}`,
            fontSize: 12, color: t.muted,
          }}>
            <span>{tr('protokoll.pagination.info', { page: meta.current_page, pages: meta.last_page, total: meta.total })}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label htmlFor={`${ids}-per-page`} style={labelStyle}>{tr('protokoll.pagination.per_page')}</label>
              <select
                id={`${ids}-per-page`}
                value={perPage}
                onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                style={{ ...selectStyle, width: 'auto', padding: '5px 8px' }}
              >
                {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <Button dark={dark} variant="secondary" size="sm" disabled={meta.current_page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
                {tr('protokoll.pagination.prev')}
              </Button>
              <Button dark={dark} variant="secondary" size="sm" disabled={meta.current_page >= meta.last_page} onClick={() => setPage(p => p + 1)}>
                {tr('protokoll.pagination.next')}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
